
import { Component, ElementRef, OnInit, ViewChild, ViewChildren, AfterViewInit, PLATFORM_ID, Inject, QueryList, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { SliderService } from './service/slider.service';
import { gsap } from 'gsap';
import { SliderData, SliderResponse } from './interfaces/slider-info.interface';
import { environment } from '../../../../environments/environment';
import { finalize } from 'rxjs';

@Component({
  selector: 'shared-slider-info',
  imports: [CommonModule],
  templateUrl: './slider-info.component.html',
  styleUrl: './slider-info.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderInfoComponent implements OnInit, AfterViewInit {
  @ViewChild('slideContainer') slideContainer!: ElementRef;
  @ViewChildren('splitText') splitTextEls!: QueryList<ElementRef>;
  @ViewChild('subitoBlock') subitoBlock!: ElementRef;
  apiBaseImages = environment.panelImagenesRepartidor + '/';
  cdr = inject(ChangeDetectorRef);
  currentSlide: number = 0;
  slideWidth: number = 0;
  autoSlideInterval: any;
  sliderImages: SliderData[] = []

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {

  }

  sliderService = inject(SliderService)

  ngOnInit() {
    // Start auto-sliding only in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.startAutoSlide();
      this.loadSliderImages();
    }
  }

  loadSliderImages() {

    this.sliderService.getSlides().pipe(finalize(() => {
      this.cdr.markForCheck();
    })).subscribe({
      next: (response: SliderResponse) => {
        this.sliderImages = response.data;
      },
      error: (error) => {

      }
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      // Get slide width after view is initialized
      this.updateSlideWidth();
      // Set initial position
      this.updateSlidePosition();

      // Update slide width on window resize
      window.addEventListener('resize', () => {
        this.updateSlideWidth();
        this.updateSlidePosition();
      });

      // Animate text blocks with GSAP using Angular refs
      this.animateSplitText();
    }
  }

  updateSlideWidth() {
    if (this.slideContainer && this.slideContainer.nativeElement) {
      this.slideWidth = this.slideContainer.nativeElement.offsetWidth / this.sliderImages.length;
    }
  }

  updateSlidePosition() {
    if (this.slideContainer && this.slideContainer.nativeElement) {
      // Hide all slides
      const slides = this.slideContainer.nativeElement.children;
      for (let i = 0; i < slides.length; i++) {
        slides[i].style.opacity = '0';
        slides[i].style.transition = 'opacity 1000ms ease-in-out';
        slides[i].style.position = 'absolute';
        slides[i].style.width = '100%';
      }
      // Show current slide
      slides[this.currentSlide].style.opacity = '1';
    }
  }

  nextSlide() {
    this.currentSlide = (this.currentSlide + 1) % this.sliderImages.length;
    this.updateSlidePosition();
    this.resetAutoSlide();
  }

  prevSlide() {
    this.currentSlide = (this.currentSlide - 1 + this.sliderImages.length) % this.sliderImages.length;
    this.updateSlidePosition();
    this.resetAutoSlide();
  }

  goToSlide(index: number) {
    this.currentSlide = index;
    this.updateSlidePosition();
    this.resetAutoSlide();
  }

  startAutoSlide() {
    if (isPlatformBrowser(this.platformId)) {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, 5000); // Change slide every 5 seconds
    }
  }

  resetAutoSlide() {
    if (isPlatformBrowser(this.platformId)) {
      clearInterval(this.autoSlideInterval);
      this.startAutoSlide();
    }
  }

  ngOnDestroy() {
    // Clear interval when component is destroyed
    if (isPlatformBrowser(this.platformId) && this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  private animateSplitText() {
    if (!isPlatformBrowser(this.platformId)) return;

    const splitBlocks = this.splitTextEls?.toArray().map(ref => ref.nativeElement) ?? [];
    const subitoBlockEl = this.subitoBlock?.nativeElement;

    if (!splitBlocks.length) return;

    gsap.set(splitBlocks, { willChange: 'transform, opacity', display: 'inline-block' });
    if (subitoBlockEl) gsap.set(subitoBlockEl, { willChange: 'transform, opacity', display: 'inline-block' });

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(
      splitBlocks,
      { y: '100%', opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.2 }
    );

    if (subitoBlockEl) {
      tl.fromTo(
        subitoBlockEl,
        { y: '100%', opacity: 0 },
        { y: 0, opacity: 1, duration: 1 },
        '-=0.3'
      );
    }
  }
}
